/*
 * Copyright (c) 2024. Dave van Rijn Development
 */
import './style/DVRCarousel.scss';

import React, {MouseEventHandler} from 'react';
import {PureComponent, ReactElement} from 'react';
import AwesomeIcon from '../icon/awesomeIcon';
import classNames from 'classnames';
import {voidFunction} from '../util/controlUtil';

interface Props {
    onClickNext: MouseEventHandler;
    onClickPrev: MouseEventHandler;
    children: ReactElement[];
    currentIndex: number;
    visibleItems: number;
    lazy: boolean;
    className: string;
    showControls: boolean;
    infinite: boolean;
    variableWidth: boolean;
}

interface State {
    initialRendered: boolean;
}

export default class DVRCarousel extends PureComponent<Props, State> {
    state: State = {
        initialRendered: false
    };
    carousel: HTMLDivElement;

    getMaxWidth = (): number => {
        const {children, visibleItems} = this.props;
        let width = 0;
        for (let i = 0; i < children.length; i++) {
            let combinedWidth = 0;
            for (let j = i; j < i + visibleItems && j < children.length; j++) {
                const container = this.getItemContainer(j);
                if (container) combinedWidth += container.offsetWidth;
            }
            if (combinedWidth > width) width = combinedWidth;
        }
        return width;
    };

    getVisibleIndexes = (): { firstIndex: number, lastIndex: number } => {
        const {currentIndex, visibleItems, infinite, children} = this.props,
            itemsBefore = Math.floor((visibleItems - 1) / 2), itemsAfter = Math.ceil((visibleItems - 1) / 2);
        let firstIndex = currentIndex - itemsBefore, lastIndex = currentIndex + itemsAfter;
        if (firstIndex < 0) {
            lastIndex = visibleItems - 1;
            firstIndex = 0;
        }
        if (lastIndex > children.length - 1) {
            if (infinite) lastIndex -= (children.length - 1);
            else {
                lastIndex = children.length - -1;
                firstIndex = Math.max(0, lastIndex - visibleItems);
            }
        }
        return {firstIndex, lastIndex};
    };

    getItemContainer = (index: number): HTMLDivElement | null => {
        return document.querySelector(`div[data-name=dvr-carousel-${index}]`);
    };

    getVisibleWidth = (): number => {
        const {firstIndex, lastIndex} = this.getVisibleIndexes();
        let width: number = 0;
        for (let i = firstIndex; i <= lastIndex; i++) {
            const container: HTMLDivElement | null = this.getItemContainer(i);
            if (container) width += container.offsetWidth;
        }
        return width;
    };

    setStyle = () => {
        if (this.carousel) {
            const {variableWidth} = this.props, {firstIndex} = this.getVisibleIndexes();
            let left: number = 0;
            if (firstIndex > 0) {
                for (let i = 0; i < firstIndex; i++) {
                    const container: HTMLDivElement | null = this.getItemContainer(i);
                    if (container) left -= container.offsetWidth;
                }
            }
            const itemsContainer: HTMLDivElement | null = this.carousel.querySelector('div.items-container');
            if (variableWidth)
                this.carousel.style.width = this.getVisibleWidth() + 'px';
            else this.carousel.style.width = this.getMaxWidth() + 'px';

            if (itemsContainer)
                itemsContainer.style.transform = `translateX(${left}px)`;

        }
        if (!this.state.initialRendered) this.setState({initialRendered: true});
    };

    renderPrevControl = () => {
        const {showControls, onClickPrev, currentIndex, infinite} = this.props;
        if (!showControls) return null;
        const disabled = currentIndex === 0 && !infinite;
        return <AwesomeIcon name='chevron-left'
                            className={classNames('carousel-control', 'prev', disabled && 'disabled')}
                            onClick={disabled ? voidFunction : onClickPrev}/>;
    };

    renderNextControl = () => {
        const {showControls, onClickNext, currentIndex, infinite, children, visibleItems} = this.props;
        if (!showControls) return null;
        const disabled = currentIndex === (children.length - 1) - visibleItems && !infinite;
        return <AwesomeIcon name='chevron-right'
                            className={classNames('carousel-control', 'next', disabled && 'disabled')}
                            onClick={disabled ? voidFunction : onClickNext}/>;
    };

    renderItems = () => {
        const {lazy, children} = this.props, {initialRendered} = this.state, {
            firstIndex, lastIndex
        } = this.getVisibleIndexes();
        return (
            <div className='carousel-items-container' ref={(ref: HTMLDivElement) => {
                this.carousel = ref;
            }}>
                <div className='items-container'>
                    {children.map((child: ReactElement, index: number) => {
                        const hidden = !initialRendered || index < firstIndex || index > lastIndex;
                        return (
                            <div key={index} className={classNames('carousel-item', hidden && 'hidden')}
                                 data-name={`dvr-carousel-${index}`}>
                                {(!hidden || !lazy) && child}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    componentDidMount = () => {
        window.setTimeout(this.setStyle, 0);
    };

    componentDidUpdate = (prevProps: Props, prevState: State) => {
        const {currentIndex, visibleItems, children} = this.props, {initialRendered} = this.state;
        if (currentIndex !== prevProps.currentIndex || visibleItems !== prevProps.visibleItems || JSON.stringify(
            children) !== JSON.stringify(prevProps.children) || initialRendered !== prevState.initialRendered)
            window.setTimeout(this.setStyle, 0);
    };

    render = () => {
        return (
            <div className='dvr-carousel'>
                {this.renderPrevControl()}
                {this.renderItems()}
                {this.renderNextControl()}
            </div>
        );
    };
}